import { useCallback, useEffect, useState } from 'react';
import { useFormikContext } from 'formik';

import EditIcon from '@mui/icons-material/Edit';

import { getFragmentData } from '@e-socna/data-layer';
import type { FragmentType } from '@e-socna/data-layer';

import { useWizardContext } from '../../../../components/Wizard/contexts/WizardContext/hooks/useWizardContext.hook';
import { RightDrawer } from '../../../../components/RightDrawer';
import { useActionBlocker } from '../../../../hooks/useActionBlocker.hook';
import { AddressWithMap } from '../../../../components/AddressWithMap';
import { AddressWithMapCmp_Address_Fragment } from '../../../../components/AddressWithMap';
import { useReferencesContext } from '../../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import type { WizardSubmitHandler } from '../../../../components/Wizard/types/submit-handler.type';

import { FormVariant } from '../../../enums/variant.enum';
import { AddressForm } from '../../../Address';
import { checkIfIsAddressEmpty } from '../../../Address/utils/check-if-is-address-empty.util';
import type { AddressForm_Address } from '../../../Address/fragments/address.fragment';
import type { AddressFormTab } from '../../../Address/enums/tab.enum';

import type { AddressContext } from './types/context.type';

interface Props {
  readonly id: string;
  readonly fallbackAddress?:
    FragmentType<typeof AddressWithMapCmp_Address_Fragment> | undefined | null;
}

export const FolderBusinessEntityAddressFormStep = <C extends AddressContext>({
  fallbackAddress: pFallbackAddress,
}: Readonly<Props>) => {
  const { addressableConfigsCodeMap } = useReferencesContext();

  const fallbackAddress = getFragmentData(
    AddressWithMapCmp_Address_Fragment,
    pFallbackAddress,
  );

  const { setIsSubmitLocked } = useWizardContext();
  const { values, setFieldValue } = useFormikContext<C>();
  const { _id, address } = values;

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const openDrawer = useCallback(() => {
    setIsDrawerOpen(true);
  }, []);
  const closeDrawer = useCallback(() => {
    setIsDrawerOpen(false);
  }, []);
  const [blockableCloseDrawer, setMustBlockAction] =
    useActionBlocker(closeDrawer);

  const noAddress = checkIfIsAddressEmpty(address);
  const canInherit = !!fallbackAddress;
  const mustInherit = canInherit && noAddress;

  useEffect(() => {
    setIsSubmitLocked(noAddress && !mustInherit);

    return () => {
      setIsSubmitLocked(false);
    };
  }, [mustInherit, noAddress, setIsSubmitLocked]);

  const handleSubmit = useCallback<
    WizardSubmitHandler<AddressForm_Address, AddressFormTab>
  >(
    ({ values, formikHelpers: { setSubmitting } }) => {
      void setFieldValue('address', values);
      setSubmitting(false);
      closeDrawer();
      return undefined;
    },
    [closeDrawer, setFieldValue],
  );

  const handleFlush = useCallback(() => {
    void setFieldValue('address', null);
  }, [setFieldValue]);

  const handleCustomize = useCallback(() => {
    void setFieldValue('address', fallbackAddress);
  }, [fallbackAddress, setFieldValue]);

  return (
    <>
      <AddressWithMap
        actionsEnabled
        address={address}
        editAction={
          !mustInherit
            ? {
                id: 'editFolderBusinessEntityAdress',
                icon: <EditIcon />,
                name: "Éditer l'Adresse",
                onClick: openDrawer,
              }
            : undefined
        }
        fallbackAddress={pFallbackAddress}
        id={_id}
        onCustomize={handleCustomize}
        onFlush={handleFlush}
        onStartCreate={openDrawer}
      />
      <RightDrawer isOpen={isDrawerOpen} onDrawerClose={blockableCloseDrawer}>
        <AddressForm
          askMemoBeforeSubmit={false}
          config={addressableConfigsCodeMap.FolderBusinessEntity}
          formVariant={
            noAddress ? FormVariant.Creation : FormVariant.Modification
          }
          initialValues={address}
          onCancel={blockableCloseDrawer}
          onSubmit={handleSubmit}
          setMustBlockAction={setMustBlockAction}
          showCancelButton
          title="Formulaire d'édition de l'Adresse"
        />
      </RightDrawer>
    </>
  );
};
